// src/tabs/calendar/TodayView.jsx — DA Groupe 3 Lot L9 (maquette #2)
//
// Vue "Aujourd'hui" : remplace MensuelView dans CalendarPanel.
// Maquette #2 (DA) :
//   - Carte « pouls du jour » MARINE en tête : date + compteurs
//     rotations (corail, mis en avant) / départs / arrivées (ciel/blanc).
//   - Filtre rapide segmented ciel : Tout / Rotations / Départs / Arrivées
//     (état LOCAL purement présentationnel — masque/montre des lignes déjà
//     calculées ; ne modifie AUCUNE donnée ni AUCUN compteur).
//   - Timeline chronologique : rotations en tête, puis le reste (départs ASC
//     puis arrivées triées par heure d'arrivée — tri sous-jacent intact).
//     Chaque entrée = bille couleur source + heure à gauche, ligne lisible.
//   - Badges source DÉTACHÉS colorés (« Airbnb » / « Booking » / « Direct »)
//     à côté du nom voyageur (toujours visible).
//
// GATE DA — 0 logique/data métier : ni fetch, ni calcul des compteurs, ni tri
//   chronologique sous-jacent (helpers _today-helpers intacts), ni handlers de
//   clic, ni data-testid. Les compteurs du pouls restent calculés sur TOUTES
//   les données (jamais filtrés). Le filtre rapide = présentation seule.
//
// Sources : reservations + pricing_blocks "direct interne FBZ" (guest_first_name présent).
// Click carte → onSelectItem({id, kind}) (F-5 ADR-105 unifié résa+block).

import { useMemo, useState } from "react"
import { Sunrise, CalendarDays, User, ArrowRightLeft, LogOut, LogIn, Sparkles, Palmtree } from "lucide-react"
import { DS } from "../../lib/ui"
import GuestTimesPills from "../../components/GuestTimesPills"
import {
  buildMovementsFromSources,
  getTodayMovements,
  detectRotations,
  flattenBlocksMap,
} from "./_today-helpers"
import { SOURCE_BG, SOURCE_BG_FALLBACK } from "./_source-colors"

// Badge source DÉTACHÉ : libellé lisible + couleur source (DS _source-colors).
//   Airbnb  = corail   · Booking = marine · Direct = vert
const SOURCE_LABEL = { airbnb: "Airbnb", booking: "Booking", direct: "Direct" }

function sourceLabelOf(source) {
  if (SOURCE_LABEL[source]) return SOURCE_LABEL[source]
  if (typeof source === "string" && source.length > 0) {
    return source.charAt(0).toUpperCase() + source.slice(1)
  }
  return "Autre"
}

function formatDayLabel(d, isToday) {
  // L'icône (Sunrise / CalendarDays) est rendue à part ; ce helper ne renvoie
  // que le texte. isToday → "Aujourd'hui — Mardi 13 mai 2026".
  const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"]
  const months = [
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
  ]
  const datePart = `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
  return isToday ? `Aujourd'hui — ${datePart}` : datePart
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

/** Badge source détaché coloré (« Airbnb » / « Booking » / « Direct »). */
function SourceBadge({ source }) {
  const color = SOURCE_BG[source] || SOURCE_BG_FALLBACK
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: "#fff",
        background: color,
        borderRadius: 5,
        padding: "2px 7px",
        letterSpacing: 0.3,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {sourceLabelOf(source)}
    </span>
  )
}

/**
 * Ligne timeline cliquable (check-out OU check-in).
 * Layout : [heure] [bille source] [direction + logement / nom + badge source].
 *
 * @param {object} item — item normalisé (cf. _today-helpers)
 * @param {'checkout'|'checkin'} variant
 * @param {Map} logementsById
 * @param {(payload:{id,kind})=>void} onSelectItem
 */
function MovementLine({ item, variant, logementsById, onSelectItem }) {
  const isCheckout = variant === "checkout"
  const logementLabel = logementsById?.get?.(item.logement_id) || item.logement_id
  const guestName =
    [item.guest_first_name, item.guest_last_name].filter(Boolean).join(" ") || "—"
  const sourceColor = SOURCE_BG[item.source] || SOURCE_BG_FALLBACK
  const timeLabel = isCheckout
    ? "—" // departure_time non chargé par loadResas SELECT (cf. CalendarPanel.jsx) → "—"
    : item.arrival_time
      ? item.arrival_time.slice(0, 5)
      : "—"
  const DirIcon = isCheckout ? LogOut : LogIn
  const dirColor = isCheckout ? DS.marine : DS.success
  const dirLabel = isCheckout ? "Départ" : "Arrivée"

  return (
    <button
      type="button"
      onClick={() => onSelectItem({ id: item.id, kind: item.kind })}
      title={`${logementLabel} · ${guestName} · ${dirLabel} ${timeLabel}`}
      className="fbz-card"
      data-direction={variant}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        width: "100%",
        textAlign: "left",
        border: "1px solid #e8edf4",
        // Code couleur DIRECTION (décision Dimitri 2026-07-09) : le liseré
        // gauche reprend la couleur du libellé — arrivée = vert DS.success,
        // départ = marine (pas rouge : un départ n'est pas une alerte, et
        // rouge/vert pénalise les daltoniens). La SOURCE (Airbnb/Booking)
        // reste lisible sur la bille + le badge.
        borderLeft: `4px solid ${dirColor}`,
        borderRadius: 10,
        padding: "10px 12px",
        // Arrivée : fond très légèrement teinté vert — #F4FAF8 = blanc +
        // 5 % du token DS.success (#1D9E75), pré-mélangé (fond OPAQUE : pas
        // de translucidité qui varierait selon ce qu'il y a derrière).
        // Départ : blanc inchangé.
        background: isCheckout ? "#fff" : "#F4FAF8",
        cursor: "pointer",
        fontFamily: DS.fontBody,
      }}
    >
      {/* Heure à gauche */}
      <span
        style={{
          flexShrink: 0,
          width: 46,
          textAlign: "right",
          fontFamily: DS.fontDisplay,
          fontSize: 14,
          fontWeight: 700,
          color: DS.marineDark,
          fontVariantNumeric: "tabular-nums",
          paddingTop: 1,
        }}
      >
        {timeLabel}
      </span>
      {/* Bille couleur source */}
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: sourceColor,
          marginTop: 4,
        }}
      />
      {/* Contenu */}
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: dirColor, textTransform: "uppercase", letterSpacing: 0.4 }}>
          <DirIcon size={13} strokeWidth={2.2} color={dirColor} style={{ flexShrink: 0 }} />
          {dirLabel}
          <span style={{ color: DS.textMuted, fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>· {logementLabel}</span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <User size={13} strokeWidth={2} color={DS.textMuted} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: DS.marineDark, overflowWrap: "anywhere" }}>
            {guestName}
          </span>
          <SourceBadge source={item.source} />
        </span>
        {/* Heures RÉELLES voyageur (formulaire de bienvenue) — ADMIN only
            (ADR-226) : cette vue n'est montée que dans CalendarPanel (tab
            'calendrier', catégorie isAdmin). GuestTimesPills ne rend rien si
            aucune heure n'est renseignée. */}
        <GuestTimesPills
          arrival={item.guest_arrival_time}
          departure={item.guest_departure_time}
          style={{ marginTop: 1 }}
        />
      </span>
    </button>
  )
}

/**
 * Entrée rotation (départ + arrivée même logement, même jour) — mise en avant
 * corail avec badge « Ménage urgent ». Contient deux MovementLine cliquables.
 */
function RotationEntry({ rotation, logementsById, onSelectItem }) {
  const logementLabel =
    logementsById?.get?.(rotation.logement_id) || rotation.logement_id
  return (
    <div
      style={{
        border: `1px solid ${DS.corailSoft}`,
        borderLeft: `4px solid ${DS.corail}`,
        borderRadius: 12,
        background: DS.corailSoft,
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: DS.fontDisplay, fontSize: 13, fontWeight: 700, color: DS.corailText }}>
          <ArrowRightLeft size={15} strokeWidth={2.2} color={DS.corail} aria-hidden="true" />
          Rotation
          <span style={{ color: DS.textMuted, fontFamily: DS.fontBody, fontWeight: 600 }}>· {logementLabel}</span>
        </span>
        <span
          aria-label="Ménage urgent — rotation départ + arrivée même jour"
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: DS.corail,
            color: "#fff",
            borderRadius: 7,
            padding: "3px 9px",
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          <Sparkles size={13} strokeWidth={2.2} color="#fff" aria-hidden="true" />
          Ménage urgent
        </span>
      </div>
      <MovementLine
        item={rotation.checkout}
        variant="checkout"
        logementsById={logementsById}
        onSelectItem={onSelectItem}
      />
      <MovementLine
        item={rotation.checkin}
        variant="checkin"
        logementsById={logementsById}
        onSelectItem={onSelectItem}
      />
    </div>
  )
}

/** Bloc compteur du pouls du jour. accent=true → mis en avant corail. */
function PulseStat({ value, label, accent }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: accent ? DS.corail : "rgba(255,255,255,0.12)",
        border: accent ? "none" : "1px solid rgba(255,255,255,0.20)",
        borderRadius: 12,
        padding: "10px 8px",
        textAlign: "center",
      }}
    >
      <div style={{ fontFamily: DS.fontDisplay, fontSize: 26, fontWeight: 700, color: "#fff", lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: accent ? "#fff" : DS.ciel, marginTop: 5, letterSpacing: 0.3 }}>
        {label}
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div
      style={{
        padding: "40px 20px",
        textAlign: "center",
        color: "#666",
        fontFamily: DS.fontBody,
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
        <Palmtree size={32} strokeWidth={1.6} color={DS.ciel} aria-hidden="true" />
      </div>
      <div style={{ fontFamily: DS.fontDisplay, fontSize: 15, fontWeight: 600, color: DS.marineDark, marginBottom: 4 }}>
        Aucun mouvement aujourd'hui
      </div>
      <div style={{ fontSize: 12, color: "#888" }}>Profitez du calme !</div>
    </div>
  )
}

const FILTERS = [
  { key: "tout", label: "Tout" },
  { key: "rotations", label: "Rotations" },
  { key: "departs", label: "Départs" },
  { key: "arrivees", label: "Arrivées" },
]

/**
 * Vue principale "Aujourd'hui".
 *
 * @param {object} props
 * @param {Date} props.today — date du jour figée au mount
 * @param {Array} props.filtered — réservations filtrées (CalendarPanel)
 * @param {Map} props.blocksByLogement — Map<logId, Array<block>>
 * @param {Map} props.logementsById — Map<logId, nom>
 * @param {(payload:{id,kind})=>void} props.onSelectItem — F-5 ADR-105 unifié
 */
export default function TodayView({
  today,
  selectedDay,
  isToday = true,
  onPrevDay,
  onNextDay,
  canPrevDay = false,
  canNextDay = false,
  filtered,
  blocksByLogement,
  logementsById,
  onSelectItem,
}) {
  // Filtre rapide — état LOCAL purement présentationnel (masque/montre des
  // lignes déjà calculées ; n'altère ni les données ni les compteurs).
  const [quickFilter, setQuickFilter] = useState("tout")

  // W1 — jour affiché = selectedDay (nav jour bornée au mois), fallback `today`.
  const day = selectedDay || today
  const todayISO = useMemo(() => toISODate(day), [day])

  const movements = useMemo(() => {
    const allBlocks = flattenBlocksMap(blocksByLogement)
    return buildMovementsFromSources(filtered, allBlocks)
  }, [filtered, blocksByLogement])

  const { rotations, soloCheckouts, soloCheckins } = useMemo(() => {
    const todayMov = getTodayMovements(movements, todayISO)
    return detectRotations(todayMov)
  }, [movements, todayISO])

  // Compteurs — calculés sur TOUTES les données (jamais filtrés).
  const totalCheckouts = rotations.length + soloCheckouts.length
  const totalCheckins = rotations.length + soloCheckins.length
  const totalMovements = totalCheckouts + totalCheckins

  // Visibilité timeline selon le filtre rapide (présentation seule).
  const showRotations = quickFilter === "tout" || quickFilter === "rotations"
  const showCheckouts = quickFilter === "tout" || quickFilter === "departs"
  const showCheckins = quickFilter === "tout" || quickFilter === "arrivees"
  const visibleCount =
    (showRotations ? rotations.length : 0) +
    (showCheckouts ? soloCheckouts.length : 0) +
    (showCheckins ? soloCheckins.length : 0)

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* ── Carte « pouls du jour » MARINE ───────────────────────────── */}
      <div
        className="fbz-card"
        style={{
          background: DS.marine,
          borderRadius: 16,
          padding: "16px 16px 14px",
          marginBottom: 16,
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <button
            onClick={onPrevDay}
            disabled={!canPrevDay}
            aria-label="Jour précédent"
            className="fbz-tap"
            style={{
              background: canPrevDay ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.06)",
              color: canPrevDay ? "#fff" : "rgba(255,255,255,0.35)",
              border: "none", borderRadius: 8, padding: "4px 12px", fontSize: 15, fontWeight: 700,
              cursor: canPrevDay ? "pointer" : "default",
            }}
          >←</button>
          <div style={{ display: "flex", alignItems: "center", gap: 9, fontFamily: DS.fontDisplay, fontSize: 17, fontWeight: 600 }}>
            {isToday
              ? <Sunrise size={19} strokeWidth={2} color={DS.corail} aria-hidden="true" />
              : <CalendarDays size={19} strokeWidth={2} color={DS.ciel} aria-hidden="true" />}
            {formatDayLabel(day, isToday)}
          </div>
          <button
            onClick={onNextDay}
            disabled={!canNextDay}
            aria-label="Jour suivant"
            className="fbz-tap"
            style={{
              background: canNextDay ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.06)",
              color: canNextDay ? "#fff" : "rgba(255,255,255,0.35)",
              border: "none", borderRadius: 8, padding: "4px 12px", fontSize: 15, fontWeight: 700,
              cursor: canNextDay ? "pointer" : "default",
            }}
          >→</button>
          {!isToday && (
            <span style={{ fontSize: 11, color: DS.ciel, fontStyle: "italic" }}>
              (bouton « Aujourd'hui » pour revenir)
            </span>
          )}
        </div>
        {/* Compteurs — rotations en corail (mises en avant) */}
        <div style={{ display: "flex", gap: 8 }}>
          <PulseStat value={rotations.length} label={rotations.length > 1 ? "Rotations" : "Rotation"} accent />
          <PulseStat value={totalCheckouts} label={totalCheckouts > 1 ? "Départs" : "Départ"} />
          <PulseStat value={totalCheckins} label={totalCheckins > 1 ? "Arrivées" : "Arrivée"} />
        </div>
      </div>

      {totalMovements === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* ── Filtre rapide segmented ciel (présentation seule) ──────── */}
          <div
            role="group"
            aria-label="Filtre rapide des mouvements"
            style={{
              display: "flex",
              gap: 4,
              background: DS.cielSoft,
              borderRadius: 10,
              padding: 4,
              marginBottom: 16,
            }}
          >
            {FILTERS.map((f) => {
              const active = quickFilter === f.key
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setQuickFilter(f.key)}
                  aria-pressed={active}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    borderRadius: 7,
                    padding: "7px 6px",
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: DS.fontBody,
                    cursor: "pointer",
                    background: active ? DS.ciel : "transparent",
                    color: active ? DS.marineDark : DS.textSecondary,
                    boxShadow: active ? "0 1px 3px rgba(33,83,135,0.18)" : "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {f.label}
                </button>
              )
            })}
          </div>

          {/* ── Timeline : rotations en tête, puis le reste ────────────── */}
          {visibleCount === 0 ? (
            <div style={{ padding: "24px 16px", textAlign: "center", fontSize: 12, color: DS.textMuted }}>
              Aucun mouvement pour ce filtre.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {showRotations &&
                rotations.map((r) => (
                  <RotationEntry
                    key={`rot-${r.logement_id}-${r.checkout.id}-${r.checkin.id}`}
                    rotation={r}
                    logementsById={logementsById}
                    onSelectItem={onSelectItem}
                  />
                ))}
              {showCheckouts &&
                soloCheckouts.map((c) => (
                  <MovementLine
                    key={`co-${c.id}`}
                    item={c}
                    variant="checkout"
                    logementsById={logementsById}
                    onSelectItem={onSelectItem}
                  />
                ))}
              {showCheckins &&
                soloCheckins.map((c) => (
                  <MovementLine
                    key={`ci-${c.id}`}
                    item={c}
                    variant="checkin"
                    logementsById={logementsById}
                    onSelectItem={onSelectItem}
                  />
                ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
